import { useState, useEffect } from "react";
import { supabase } from "@/integrations/supabase/client";
import { cn } from "@/lib/utils";

interface BrandLogo {
  id: string;
  name: string;
  image_url: string;
  alt_text: string | null;
  link_url: string | null;
  logo_style: string;
}

interface BrandShowcaseProps {
  site?: "bbq";
  title?: string;
  subtitle?: string;
  className?: string;
  variant?: "light" | "dark";
}

const defaultConfig = {
  bbq: {
    title: "Premium Brands We Carry",
    subtitle: "Top grills, components, and outdoor kitchen products.",
  },
};

export default function BrandShowcase({
  site = "bbq",
  title,
  subtitle,
  className,
  variant = "light",
}: BrandShowcaseProps) {
  const [logos, setLogos] = useState<BrandLogo[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchLogos = async () => {
      try {
        const column = "show_on_bbq";
        const { data, error } = await supabase
          .from("brand_logos")
          .select("id, name, image_url, alt_text, link_url, logo_style")
          .eq("enabled", true)
          .eq(column, true)
          .order("sort_order", { ascending: true });

        if (error) throw error;
        setLogos(data || []);
      } catch (err) {
        console.error("Error fetching brand logos:", err);
      } finally {
        setLoading(false);
      }
    };

    fetchLogos();
  }, [site]);

  if (loading) {
    return (
      <section className={cn("py-16", className)}>
        <div className="container mx-auto px-4">
          <div className="flex justify-center">
            <div className="animate-pulse flex gap-8">
              {[1, 2, 3, 4, 5].map((i) => (
                <div key={i} className="w-24 h-12 bg-gray-200 rounded" />
              ))}
            </div>
          </div>
        </div>
      </section>
    );
  }

  if (logos.length === 0) {
    return null;
  }

  const displayTitle = title || defaultConfig[site].title;
  const displaySubtitle = subtitle || defaultConfig[site].subtitle;

  const isDark = variant === "dark";

  return (
    <section
      className={cn(
        "py-16 md:py-20",
        isDark ? "bg-gray-900" : "bg-white",
        className
      )}
    >
      <div className="container mx-auto px-4">
        {/* Header */}
        <div className="text-center mb-12">
          <h2
            className={cn(
              "text-2xl md:text-3xl font-semibold mb-3",
              isDark ? "text-white" : "text-gray-900"
            )}
          >
            {displayTitle}
          </h2>
          <p
            className={cn(
              "text-base md:text-lg max-w-2xl mx-auto",
              isDark ? "text-gray-400" : "text-gray-600"
            )}
          >
            {displaySubtitle}
          </p>
        </div>

        {/* Logo Grid */}
        <div className="flex flex-wrap justify-center items-center gap-8 md:gap-12 lg:gap-16">
          {logos.map((logo) => {
            const LogoContent = (
              <div
                className={cn(
                  "flex items-center justify-center px-4 py-2 transition-all duration-300",
                  "hover:scale-105 hover:opacity-80",
                  logo.link_url && "cursor-pointer"
                )}
              >
                <img
                  src={logo.image_url}
                  alt={logo.alt_text || logo.name}
                  className={cn(
                    "h-10 md:h-12 lg:h-14 w-auto max-w-[140px] md:max-w-[160px] object-contain",
                    // Apply filter based on logo_style and variant
                    logo.logo_style === "dark" && isDark && "invert brightness-200",
                    logo.logo_style === "light" && !isDark && "invert",
                    logo.logo_style === "auto" && isDark && "brightness-0 invert"
                  )}
                  loading="lazy"
                />
              </div>
            );

            if (logo.link_url) {
              return (
                <a
                  key={logo.id}
                  href={logo.link_url}
                  target="_blank"
                  rel="noopener noreferrer"
                  title={`Visit ${logo.name}`}
                >
                  {LogoContent}
                </a>
              );
            }

            return <div key={logo.id}>{LogoContent}</div>;
          })}
        </div>
      </div>
    </section>
  );
}
